/**
 * GoogleCallbackPage – handles the redirect back from Google OAuth.
 *
 * Google redirects to:
 *   /auth/google/callback?code=...&state=...
 *
 * This page extracts the query params, forwards them to the backend,
 * and on success stores the JWT tokens and navigates to /learn.
 */
import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { apiErrorMessage, authApi } from '../../api/client'
import { useAuthStore } from '../../store/authStore'
import toast from 'react-hot-toast'

export default function GoogleCallbackPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { setTokens, setUser } = useAuthStore()
  const called = useRef(false)  // prevent double-call in React strict mode

  useEffect(() => {
    if (called.current) return
    called.current = true

    const code  = params.get('code')
    const state = params.get('state')
    const error = params.get('error')

    if (error) {
      toast.error(`Google sign-in cancelled: ${error}`)
      navigate('/login', { replace: true })
      return
    }

    if (!code || !state) {
      toast.error('Invalid Google callback — missing parameters')
      navigate('/login', { replace: true })
      return
    }

    authApi.googleCallback(code, state)
      .then(async (res) => {
        setTokens(res.data.access_token, res.data.refresh_token)
        const me = await authApi.me()
        setUser(me.data)
        useAuthStore.getState().setMustChangePassword(res.data.must_change_password)
        if (res.data.must_change_password) {
          toast('Change the initial password before continuing.')
          navigate('/change-password', { replace: true })
          return
        }
        if (res.data.course_slug) {
          toast.success(res.data.course_access_granted
            ? 'Signup complete. You are enrolled in your selected course.'
            : 'Signup complete. Paid course access requires payment approval.')
          navigate(`/learn/${encodeURIComponent(res.data.course_slug)}`, { replace: true })
          return
        }
        if (res.data.is_new_user) {
          toast.success('Account created with Google — welcome to PacketFive Academy!')
        } else {
          toast.success('Signed in with Google!')
        }
        navigate('/learn', { replace: true })
      })
      .catch((e) => {
        useAuthStore.getState().logout()
        const message = apiErrorMessage(e, 'Google sign-in failed')
        toast.error(message)
        navigate(message.startsWith('No registered account.') || message.startsWith('Complete signup') ? '/register' : '/login', { replace: true })
      })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue mx-auto mb-4" />
        <p className="text-brand-muted">Completing Google sign-in…</p>
      </div>
    </div>
  )
}
